import Box from '@mui/material/Box';
import React, { useState, useEffect, useRef } from 'react';
import { StacklineLogoBlueIcon } from '../SvgIcons/SvgIcons';
import AuthButton from './AuthButton';
import AuthContainer from './AuthContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import LogoContainer from './LogoContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useStacklineTheme } from '@stackline/ui';
import { getAppDisplayName, getAppName } from 'src/utils/app';
import { LoginContainerConfigs } from 'src/components/SignIn/SignInContainer';
import { AnimatedEllipsis } from 'src/components/common/Loading/AnimatedEllipsis';
import colors from 'src/utils/colors';

interface OtpVerifyProps {
  email?: string;
  onVerify: (code: string) => void;
  onResend: () => void;
  loading: boolean;
  error?: string;
}

const RESEND_COOLDOWN_SECONDS = 60;

const OtpVerify: React.FC<OtpVerifyProps> = ({ onVerify, onResend, loading, error }) => {
  const appName = getAppName();
  const title = getAppDisplayName(appName);
  const textColor = LoginContainerConfigs?.[appName]?.textColor;
  const theme = useStacklineTheme();

  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length === 6 && !loading) {
      onVerify(code);
    }
  };

  const handleResend = () => {
    if (countdown > 0) return;
    setCountdown(RESEND_COOLDOWN_SECONDS);
    setCode('');
    onResend();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(val);
  };

  return (
    <AuthContainer>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Flex flexDirection="column" gap={40}>
          <LogoContainer title={title} />

          <Flex flexDirection="column" gap={24}>
            <Box>
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={handleChange}
                placeholder="Enter 6-digit code"
                style={{
                  width: '100%',
                  height: '48px',
                  borderRadius: '999px',
                  border: `1px solid ${error ? '#F35379' : colors.graysSecondary}`,
                  padding: '0 20px',
                  fontSize: '14px',
                  fontFamily: "'Geist', Roboto, sans-serif",
                  fontWeight: 600,
                  letterSpacing: '4px',
                  textAlign: 'center',
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: '#fff',
                  color: colors.darkBlue
                }}
              />
              {error && (
                <Text
                  variant="body2"
                  sx={{ fontFamily: "'Geist', Roboto, sans-serif", color: '#F35379', marginTop: '8px', fontSize: '13px' }}
                >
                  {error}
                </Text>
              )}
            </Box>
          </Flex>

          <AuthButton disabled={loading || code.length !== 6} type="submit">
            {loading ? (
              <Box
                display="flex"
                flexDirection="row"
                maxHeight="100%"
                alignItems="center"
                justifyContent="center"
                fontWeight="600"
                fontFamily="Geist"
              >
                Verifying
                <AnimatedEllipsis width={40} height={40} style={{ margin: 0, transform: 'translate(-8px,5px)' }} />
              </Box>
            ) : (
              'Continue'
            )}
          </AuthButton>
        </Flex>

        <Box mt="24px" display="flex" justifyContent="center">
          <Text variant="body2" sx={{ fontFamily: 'Geist', color: colors.grey, fontSize: '13px' }}>
            Didn&apos;t receive a code?{' '}
            {countdown > 0 ? (
              <span style={{ color: colors.grey }}>Resend in {countdown}s</span>
            ) : (
              <span
                onClick={handleResend}
                style={{ color: textColor || '#46A8F6', fontWeight: 500, cursor: 'pointer' }}
              >
                Resend code
              </span>
            )}
          </Text>
        </Box>

        <Box mt={theme.spacing.xxl} gap="12px" display="flex" flexDirection="column" alignItems="center">
          <StacklineLogoBlueIcon width="97.1px" />
          <Text variant="body2" sx={{ fontFamily: 'Geist', fontWeight: 500 }}>
            Don&apos;t have an account?{' '}
            <a style={{ color: textColor }} href="https://www.stackline.com/contact">
              Contact us
            </a>
          </Text>
        </Box>
      </form>
    </AuthContainer>
  );
};

export default OtpVerify;
